import type { MaybeValidProps } from "@core/domain/models/maybe-valid";
import type { Unbrand } from "@core/domain/types/unbrand.type";
import type { Id } from "@core/domain/value-objects/id.value-object";
import { zIdSchema } from "@core/domain/value-objects/id.value-object";
import type { ModelError, PartialValid } from "@iot-platform/core";
import {
    NonRecuperableModelError,
    RecuperableModelError,
    partialSafeParse,
} from "@iot-platform/core";
import { NotebookMessage } from "@notebook/domain/models/notebook-message.model";
import { merge } from "es-toolkit";
import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import * as z from "zod";

const NotebookStationSchema = z
    .object({
        id: z.number(),
        name: z.string().trim(),
    })
    .readonly();
export type NotebookStation = z.output<typeof NotebookStationSchema>;

const NotebookCropTypeSchema = z
    .object({
        id: z.number(),
        code: z.string().trim(),
    })
    .readonly();
export type NotebookCropType = z.output<typeof NotebookCropTypeSchema>;

const NotebookCampaignSchema = z
    .object({
        id: z.number().positive(),
        name: z.string().trim(),
        creator: z.object({
            id: z.number().positive(),
            email: z.string().trim(),
        }),
        startDate: z.string().trim(),
        endDate: z.string().trim(),
    })
    .readonly();
export type NotebookCampaign = z.output<typeof NotebookCampaignSchema>;

const NotebookSchema = z
    .object({
        id: zIdSchema,
        stations: z.array(NotebookStationSchema),
        campaign: NotebookCampaignSchema,
        cropType: NotebookCropTypeSchema,
        notebookMessages: z.array(
            z.custom<NotebookMessage>((val) => val instanceof NotebookMessage, {
                error: "Invalid NotebookMessage instance",
            }),
        ),
    })
    .brand<"Notebook">();

export type NotebookInputProps = z.input<typeof NotebookSchema>;
export type NotebookProps = z.output<typeof NotebookSchema>;

export class Notebook {
    private constructor(private readonly props: Readonly<NotebookProps>) {}

    get id(): Id {
        return this.props.id;
    }

    get stations(): readonly NotebookStation[] {
        return this.props.stations;
    }

    get campaign(): NotebookCampaign {
        return this.props.campaign;
    }

    get cropType(): NotebookCropType {
        return this.props.cropType;
    }

    get notebookMessages(): readonly NotebookMessage[] {
        return this.props.notebookMessages;
    }

    static validate(
        props: Readonly<MaybeValidProps<NotebookInputProps>>,
    ): Result<NotebookProps, z.ZodError<NotebookProps>> {
        const result = NotebookSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    private static partialValidate(
        props: Readonly<MaybeValidProps<NotebookInputProps>>,
    ): Result<NotebookProps, PartialValid<typeof Notebook>> {
        const result = partialSafeParse(NotebookSchema, props);

        return result.success
            ? ok(result.data)
            : err({
                  validProps: result.validData,
                  errors: result.error,
              });
    }

    static create(
        props: Readonly<NotebookInputProps>,
    ): Result<Notebook, z.ZodError<NotebookProps>> {
        return Notebook.validate(props).map(
            (validatedProps) => new Notebook(validatedProps),
        );
    }

    static createWithRecovery(
        props: NotebookInputProps,
    ): Result<Notebook, ModelError<typeof Notebook>> {
        return Notebook.partialValidate(props)
            .mapErr((error) => {
                if (!error.validProps.id) return new NonRecuperableModelError();

                const defaultProps: Unbrand<NotebookProps> = {
                    id: error.validProps.id,
                    stations: [],
                    campaign: {
                        id: 0,
                        name: "",
                        creator: {
                            id: 0,
                            email: "",
                        },
                        startDate: "",
                        endDate: "",
                    },
                    cropType: {
                        id: 0,
                        code: "",
                    },
                    notebookMessages: [],
                };

                const recoveryProps: Unbrand<NotebookProps> = merge(
                    defaultProps,
                    error.validProps,
                );

                return new RecuperableModelError<typeof Notebook>(
                    new Notebook(recoveryProps as NotebookProps),
                    error.errors,
                );
            })
            .map((validatedProps) => new Notebook(validatedProps));
    }

    toCreateProps(): NotebookInputProps {
        return {
            id: this.props.id.toCreateProps(),
            stations: this.props.stations,
            campaign: this.props.campaign,
            cropType: this.props.cropType,
            notebookMessages: this.props.notebookMessages,
        };
    }
}
